import { ref, type Ref } from 'vue';

/** Shared phone dismissal; header gestures never compete with body scrolling. */
export function useMobileSheet(
  close: () => void,
  busy: () => boolean,
  hasDraft: () => boolean = () => false
): {
  offset: Ref<number>;
  canDismiss: () => boolean;
  requestClose: () => void;
  down: (e: PointerEvent) => void;
  move: (e: PointerEvent) => void;
  up: (e: PointerEvent) => void;
  cancel: () => void;
  markDirty: () => void;
} {
  const offset = ref(0);
  const dirty = ref(false);
  let start: { x: number; y: number; time: number; id: number } | null = null;
  const mobile = () => window.innerWidth <= 767;
  const canDismiss = () =>
    !busy() &&
    (!mobile() || (!dirty.value && !hasDraft()) || window.confirm('Discard unsaved changes?'));
  const requestClose = () => {
    if (canDismiss()) close();
  };
  const down = (e: PointerEvent) => {
    if (!mobile() || busy() || e.button !== 0) return;
    if ((e.target as HTMLElement).closest('button, input, select, textarea, a')) return;
    start = { x: e.clientX, y: e.clientY, time: e.timeStamp, id: e.pointerId };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const move = (e: PointerEvent) => {
    if (!start || e.pointerId !== start.id) return;
    offset.value = Math.max(0, e.clientY - start.y);
  };
  const up = (e: PointerEvent) => {
    if (!start || e.pointerId !== start.id) return;
    const distance = e.clientY - start.y;
    const sideways = Math.abs(e.clientX - start.x);
    const elapsed = e.timeStamp - start.time;
    const dismiss =
      distance > sideways * 1.2 &&
      (distance >= 110 || (distance >= 55 && elapsed > 0 && distance / elapsed > 0.55));
    start = null;
    offset.value = 0;
    if (dismiss) requestClose();
  };
  const cancel = () => {
    start = null;
    offset.value = 0;
  };
  const markDirty = () => {
    dirty.value = true;
  };
  return { offset, canDismiss, requestClose, down, move, up, cancel, markDirty };
}
